import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';

const HowToImproveRatio: React.FC = () => {
  useSEO(
    'How to Improve Your Instagram Follower-to-Following Ratio — Purgr',
    'A high following count hurts your Instagram credibility. Learn what a healthy follower ratio looks like, why it matters, and how to improve yours safely.'
  );

  return (
    <PageLayout title="How to Improve Your Instagram Follower-to-Following Ratio">
      <div className="space-y-8 text-white/70 text-sm leading-relaxed">

        <p className="text-white/80 text-base">
          Your follower-to-following ratio is one of the first things people notice when they visit your
          Instagram profile. A lopsided ratio — following thousands of accounts while only a few hundred
          follow you back — signals low credibility and can actively hurt your growth. Here's what the
          numbers mean and how to fix them.
        </p>

        {/* What is follower ratio */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-3 text-base">What is a follower-to-following ratio?</p>
          <p className="text-white/55 mb-3">
            Your follower ratio is simply how many followers you have compared to how many accounts you follow.
            For example:
          </p>
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
              <span className="text-green-400 font-bold w-16 flex-shrink-0">Good</span>
              <span className="text-white/60">5,000 followers / 500 following = <strong className="text-white/80">10:1 ratio</strong></span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
              <span className="text-yellow-400 font-bold w-16 flex-shrink-0">Neutral</span>
              <span className="text-white/60">1,000 followers / 800 following = <strong className="text-white/80">1.25:1 ratio</strong></span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
              <span className="text-red-400 font-bold w-16 flex-shrink-0">Poor</span>
              <span className="text-white/60">200 followers / 2,000 following = <strong className="text-white/80">0.1:1 ratio</strong></span>
            </div>
          </div>
        </div>

        {/* Why it matters */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">Why your ratio matters</p>
          <div className="space-y-3">
            {[
              { title: 'Social proof', body: 'A high following count with few followers signals that you followed many accounts hoping for a follow-back — a tactic most users recognize and distrust.' },
              { title: 'Profile credibility', body: 'When someone visits your profile, a poor ratio is a red flag. Many users won\'t follow an account that follows 5x more accounts than follow it back.' },
              { title: 'Algorithmic signals', body: 'While Instagram hasn\'t confirmed ratio as a direct ranking factor, high engagement rates (easier to achieve with a smaller, more targeted following) do influence reach.' },
              { title: 'Audience quality', body: 'Accounts you follow but who don\'t follow you back add nothing to your feed quality or engagement. Cleaning them out improves your content experience.' },
            ].map(({ title, body }) => (
              <div key={title} className="glass-card p-4 flex gap-4">
                <div className="w-8 h-8 rounded-full ig-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5 flex-none">
                  →
                </div>
                <div>
                  <p className="text-white/80 font-semibold text-sm mb-1">{title}</p>
                  <p className="text-white/50 text-xs leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How to improve */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">How to improve your ratio</p>
          <div className="space-y-4">
            {[
              {
                n: '1',
                title: 'Find out who isn\'t following you back',
                body: 'The first step is knowing who you follow that doesn\'t follow you back. Use Purgr to upload your Instagram data files and get a complete list instantly — no login required.',
              },
              {
                n: '2',
                title: 'Unfollow non-mutuals strategically',
                body: 'Go through your non-followers list and manually unfollow accounts that aren\'t relevant to you, aren\'t active, or that you don\'t genuinely want to follow. Don\'t mass-unfollow — Instagram may limit your account if you unfollow too many accounts too quickly (stay under ~200 per day).',
              },
              {
                n: '3',
                title: 'Stop follow-for-follow tactics',
                body: 'Following accounts just hoping they\'ll follow back is the main reason ratios get lopsided. Focus on following accounts you genuinely want to see content from.',
              },
              {
                n: '4',
                title: 'Grow your followers organically',
                body: 'Post consistently, use relevant hashtags, engage with accounts in your niche, and create content worth following. Growing your follower count is the other side of improving your ratio.',
              },
            ].map(({ n, title, body }) => (
              <div key={n} className="glass-card p-4 flex gap-4">
                <div className="w-8 h-8 rounded-full ig-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">
                  {n}
                </div>
                <div>
                  <p className="text-white/80 font-semibold text-sm mb-1">{title}</p>
                  <p className="text-white/50 text-xs leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tips */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-3">Things to keep in mind</p>
          <ul className="space-y-2 text-white/55 text-xs">
            <li>• Don't unfollow more than ~150–200 accounts per day to avoid Instagram action blocks.</li>
            <li>• Some accounts you follow for good reasons even if they don't follow back (brands, news, inspiration) — that's fine.</li>
            <li>• Ratio benchmarks vary by account type. A personal account and a brand account are judged differently.</li>
            <li>• Improving your ratio is a gradual process. Focus on content quality alongside cleanup.</li>
          </ul>
        </div>

        {/* CTA */}
        <div className="rounded-xl p-px ig-gradient-bg">
          <div className="rounded-xl bg-[#0D0D0D] px-6 py-6 text-center">
            <p className="text-white font-bold text-base mb-1">Start by finding your non-followers</p>
            <p className="text-white/50 text-sm mb-4">Upload your Instagram data to Purgr — free, private, instant results.</p>
            <Link
              to="/"
              className="btn-gradient inline-block text-white font-bold px-8 py-3 rounded-full"
            >
              Check Who Doesn't Follow You Back →
            </Link>
          </div>
        </div>

        {/* Related guides */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Related guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/what-is-a-good-follower-following-ratio" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">What is a good follower-to-following ratio? →</p>
            </Link>
            <Link to="/how-to-clean-up-your-following-list" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to clean up your following list →</p>
            </Link>
            <Link to="/best-free-instagram-unfollow-tracker" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">Best free Instagram unfollow tracker →</p>
            </Link>
            <Link to="/instagram-account-audit" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">Full Instagram account audit guide →</p>
            </Link>
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default HowToImproveRatio;
